import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { EFConnectableSide, FFlowModule } from '@foblex/flow';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DateTimePipe } from '../../../core/pipes/date-time.pipe';
import { EventsFlowDataFormService } from '../../services/events-flow-data-form.service';
import { hasEventsFlowEventNodeTypes } from '../models/events-flow-event-node-types';
import { FlexModule } from '@angular/flex-layout';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'workflow-node',
    templateUrl: './workflow-node.component.html',
    styleUrls: ['./workflow-node.component.scss'],
    host: {
        '[style.border-top-color]': 'model.color',
    },
    standalone: true,
    imports: [
        FlexModule,
        NgClass,
        MatIcon,
        MatTooltip,
        FFlowModule,
        NgIf,
        NgFor,
    ],
})
export class WorkflowNodeComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('nodeHead') nodeHead!: ElementRef;

  @Input({ required: true })
  public model!: any;
  @Input({ required: true })
  public isContentMinimized: boolean;

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public removeConnection: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public nodeSelected: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public readonly actionClicked = new EventEmitter<MouseEvent>();

  public outputs: string[] = [];
  public form: FormControl = new FormControl();

  public readonly eFConnectableSide = EFConnectableSide;

  private subscription$: Subscription = Subscription.EMPTY;

  constructor(
    private readonly _dateTimePipe: DateTimePipe,
    private readonly _eventsFlowDataFormService: EventsFlowDataFormService,
  ) {}

  get isSettingsFormValid(): boolean {
    if (hasEventsFlowEventNodeTypes(this.model.type)) {
      return this._eventsFlowDataFormService.isEventsFlowFiltersFormValid;
    }

    return this._eventsFlowDataFormService.isEventFlowActionItemSettingsFormValid(this.model.value?.uniqueKey);
  }

  get hasError(): boolean {
    return !!this.model?.value?.lastErrorMessage;
  }

  get isExecuted(): boolean {
    return !!this.model?.value?.lastExecutionAt;
  }

  get statusMessage(): string {
    const {
      lastExecutionAt,
      lastRequestStatusCode,
      monthlyCount,
      lastErrorMessage,
      monthlyFailCount,
      monthlySuccessCount,
    } = this.model?.value || {};

    if (!this.isExecuted) {
      return 'No executions recorded yet';
    }

    const formattedDate = lastExecutionAt ? this._dateTimePipe.transform(lastExecutionAt, 'M/dd/yyyy hh:mm a') : null;
    const statusCode = lastRequestStatusCode ? `Status Code: ${lastRequestStatusCode}` : null;
    const totalCount =
      monthlyCount !== undefined && monthlyCount !== null ? `Monthly Total Executions: ${monthlyCount}` : null;
    const successCount =
      monthlySuccessCount !== undefined && monthlySuccessCount !== null
        ? `Monthly Successful Executions: ${monthlySuccessCount}`
        : null;
    const failCount =
      monthlyFailCount !== undefined && monthlyFailCount !== null
        ? `Monthly Failed Executions: ${monthlyFailCount}`
        : null;
    const errorMessage = lastErrorMessage ? `Last Error: ${lastErrorMessage}` : null;

    return [
      formattedDate ? `Last Execution: ${formattedDate}` : null,
      statusCode,
      totalCount,
      successCount,
      failCount,
      errorMessage,
    ]
      .filter(Boolean)
      .join('\n');
  }

  public ngOnInit(): void {
    this.form = new FormControl(this.model?.value);
    this.subscription$ = this.subscribeToFormChanges();
  }

  public ngOnChanges(): void {
    this.outputs = (this.model?.outputs || []).slice().reverse();
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public onActionClicked(event: MouseEvent): void {
    this.actionClicked.next(event);
  }

  private subscribeToFormChanges(): Subscription {
    return this.form.valueChanges.subscribe(value => {
      this.valueChange.emit(value);
      setTimeout(() => {
        this.outputs = (this.model?.outputs || []).slice().reverse();
      });
    });
  }
}
